<template>
  <div class="tiptap-editor" :class="{ 'editor-rtl': dir === 'rtl' }">
    <label v-if="label" :for="id" class="editor-label">{{ label }}</label>
    
    <div v-if="!isEditorReady" class="editor-loading">
      <span class="loading-spinner"></span>
      <span>Loading editor...</span>
    </div>
    
    <template v-else>
      <!-- Editor Toolbar -->
      <div class="editor-toolbar" v-if="!hideToolbar">
        <div class="toolbar-group">
          <button
            v-for="item in formatButtons"
            :key="item.command"
            class="toolbar-btn"
            @click="handleToolbarAction(item)"
            :class="{ 'is-active': isButtonActive(item) }"
            :title="item.title"
          >
            <span v-html="item.icon"></span>
          </button>
        </div>
        
        <div class="toolbar-divider"></div>
        
        <div class="toolbar-group">
          <button
            v-for="item in headingButtons"
            :key="item.command + item.level"
            class="toolbar-btn"
            @click="handleToolbarAction(item)"
            :class="{ 'is-active': isButtonActive(item) }"
            :title="item.title"
          >
            <span v-html="item.icon"></span>
          </button>
        </div>
        
        <div class="toolbar-divider"></div>
        
        <div class="toolbar-group">
          <button
            v-for="item in listButtons"
            :key="item.command"
            class="toolbar-btn"
            @click="handleToolbarAction(item)"
            :class="{ 'is-active': isButtonActive(item) }"
            :title="item.title"
          >
            <span v-html="item.icon"></span>
          </button>
        </div>
        
        <div class="toolbar-divider"></div>
        
        <div class="toolbar-group">
          <button
            v-for="item in alignButtons"
            :key="item.command + item.value"
            class="toolbar-btn"
            @click="handleToolbarAction(item)"
            :class="{ 'is-active': isButtonActive(item) }"
            :title="item.title"
          >
            <span v-html="item.icon"></span>
          </button>
        </div>
        
        <div class="toolbar-divider"></div>
        
        <div class="toolbar-group">
          <!-- Color Picker -->
          <div class="color-picker">
            <button 
              class="toolbar-btn color-btn" 
              :title="'Text Color'"
              :style="{ '--current-color': currentColor }"
            >
              <span class="color-icon">A</span>
              <input 
                type="color" 
                v-model="currentColor" 
                @change="setColor(currentColor)"
                class="color-input"
              />
            </button>
          </div>
          
          <!-- Highlight Picker -->
          <div class="color-picker">
            <button 
              class="toolbar-btn highlight-btn" 
              :title="'Highlight Color'"
              :style="{ '--current-highlight': currentHighlight }"
            >
              <span class="highlight-icon">A</span>
              <input 
                type="color" 
                v-model="currentHighlight" 
                @change="setHighlight(currentHighlight)"
                class="color-input"
              />
            </button>
          </div>
        </div>
        
        <div class="toolbar-divider"></div>
        
        <div class="toolbar-group">
          <button
            v-for="item in insertButtons"
            :key="item.command"
            class="toolbar-btn"
            @click="handleToolbarAction(item)"
            :title="item.title"
          >
            <span v-html="item.icon"></span>
          </button>
        </div>
        
        <div class="toolbar-divider"></div>
        
        <div class="toolbar-group">
          <button
            v-for="item in historyButtons"
            :key="item.command"
            class="toolbar-btn"
            @click="handleToolbarAction(item)"
            :title="item.title"
          >
            <span v-html="item.icon"></span>
          </button>
        </div>
      </div>
      
      <!-- Editor Content -->
      <div class="editor-content-wrapper" :class="{ 'is-invalid': error }">
        <editor-content 
          :editor="editor" 
          :dir="dir" 
          @click="handleEditorClick" 
          class="editor-content" 
        />
      </div>
    </template>
    
    <div v-if="error" class="error-message">{{ error }}</div>
    <p v-if="helpText" class="help-text">{{ helpText }}</p>
    
    <!-- Link Dialog -->
    <transition name="fade">
      <div v-if="showLinkDialog" class="dialog-overlay">
        <div class="dialog-container">
          <div class="dialog-header">
            <h3>Insert Link</h3>
            <button class="close-btn" @click="showLinkDialog = false">&times;</button>
          </div>
          <div class="dialog-body">
            <input 
              type="text" 
              placeholder="https://example.com" 
              v-model="linkUrl" 
              @keyup.enter="setLink"
              class="dialog-input"
              ref="linkInput"
              autofocus
            />
          </div>
          <div class="dialog-footer">
            <button class="btn-cancel" @click="showLinkDialog = false">Cancel</button>
            <button class="btn-primary" @click="setLink">Insert</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import HardBreak from '@tiptap/extension-hard-break';
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

export default {
  name: 'QuillEditor',
  components: {
    EditorContent
  },
  props: {
    content: {
      type: String,
      default: ''
    },
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: () => `editor-${Math.random().toString(36).substring(2)}`
    },
    error: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Enter content here...'
    },
    toolbar: {
      type: Array,
      default: null
    },
    hideToolbar: {
      type: Boolean,
      default: false
    },
    minHeight: {
      type: String,
      default: '200px'
    },
    dir: {
      type: String,
      default: 'ltr'
    }
  },
  emits: ['update:modelValue', 'update:content', 'blur', 'focus', 'change'],
  setup(props, { emit }) {
    const isEditorReady = ref(false);
    const showLinkDialog = ref(false);
    const linkUrl = ref('');
    const linkInput = ref(null);
    const editor = ref(null);
    const currentColor = ref('#000000');
    const currentHighlight = ref('#FFFF00');

    // Get content from either modelValue or content props
    const initialContent = computed(() => props.content || props.modelValue || '');

    // Format Buttons (Bold, Italic, etc.)
    const formatButtons = computed(() => [
      { command: 'toggleBold', icon: '<strong>B</strong>', title: 'Bold' },
      { command: 'toggleItalic', icon: '<em>I</em>', title: 'Italic' },
      { command: 'toggleUnderline', icon: '<u>U</u>', title: 'Underline' },
      { command: 'toggleStrike', icon: '<s>S</s>', title: 'Strike' }
    ]);

    // Heading Buttons
    const headingButtons = computed(() => [
      { command: 'toggleHeading', level: 1, icon: 'H1', title: 'Heading 1' },
      { command: 'toggleHeading', level: 2, icon: 'H2', title: 'Heading 2' }
    ]);

    // List Buttons
    const listButtons = computed(() => [
      { command: 'toggleBulletList', icon: '• List', title: 'Bullet List' },
      { command: 'toggleOrderedList', icon: '1. List', title: 'Ordered List' }
    ]);

    // Alignment Buttons
    const alignButtons = computed(() => [
      { command: 'setTextAlign', value: 'left', icon: '←', title: 'Align Left' },
      { command: 'setTextAlign', value: 'center', icon: '↔', title: 'Align Center' },
      { command: 'setTextAlign', value: 'right', icon: '→', title: 'Align Right' }
    ]);

    // Insert Buttons
    const insertButtons = computed(() => [
      { command: 'setLink', icon: '🔗', title: 'Insert Link' },
      { command: 'addImage', icon: '🖼️', title: 'Insert Image' },
      { command: 'addTable', icon: 'Table', title: 'Insert Table' },
      { command: 'addHardBreak', icon: '↵', title: 'Line Break' }
    ]);

    // History Buttons
    const historyButtons = computed(() => [
      { command: 'undo', icon: '↩️', title: 'Undo' },
      { command: 'redo', icon: '↪️', title: 'Redo' }
    ]);

    onMounted(() => {
      // Set up the editor with the initial content and RTL support if needed
      editor.value = new Editor({
        content: initialContent.value,
        extensions: [
          StarterKit,
          Underline,
          TextStyle,
          Color,
          Highlight.configure({
            multicolor: true,
          }),
          Link.configure({
            openOnClick: false,
            validate: href => /^https?:\/\//.test(href),
          }),
          Image,
          Placeholder.configure({
            placeholder: props.placeholder,
          }),
          TextAlign.configure({
            types: ['heading', 'paragraph'],
            defaultAlignment: props.dir === 'rtl' ? 'right' : 'left',
          }),
          Table.configure({
            resizable: true,
          }),
          TableRow,
          TableCell,
          TableHeader,
          HardBreak.configure({
            keymaps: ['Shift-Enter', 'Enter'],
            HTMLAttributes: {
              class: 'hard-break'
            },
            parseHTML() {
              return [
                { tag: 'br' },
                { tag: 'br.hard-break' }
              ]
            },
          }),
        ],
        onUpdate: ({ editor }) => {
          const html = editor.getHTML();
          emit('update:modelValue', html);
          emit('update:content', html);
          emit('change', html);
        },
        onBlur: ({ event }) => {
          emit('blur', event);
        },
        onFocus: ({ event }) => {
          emit('focus', event);
        },
      });

      // Apply RTL direction to the editor if needed
      if (props.dir === 'rtl') {
        editor.value.view.dom.dir = 'rtl';
      }

      // Ensure the editor is properly initialized
      setTimeout(() => {
        isEditorReady.value = true;
      }, 100);
    });

    onBeforeUnmount(() => {
      if (editor.value) {
        editor.value.destroy();
      }
    });

    // Watch for external content changes
    watch(() => props.content || props.modelValue, (newContent) => {
      if (editor.value && newContent !== editor.value.getHTML()) {
        editor.value.commands.setContent(newContent);
      }
    });

    // Watch for direction changes
    watch(() => props.dir, (newDir) => {
      if (editor.value && editor.value.view) {
        editor.value.view.dom.dir = newDir;
        
        // Reset alignment based on direction
        if (newDir === 'rtl') {
          editor.value.chain().focus().setTextAlign('right').run();
        } else {
          editor.value.chain().focus().setTextAlign('left').run();
        }
      }
    });

    // Watch for link dialog visibility and focus input when shown
    watch(showLinkDialog, (newValue) => {
      if (newValue) {
        nextTick(() => {
          if (linkInput.value) {
            linkInput.value.focus();
          }
        });
      }
    });

    // Handle toolbar button clicks
    const handleToolbarAction = (item) => {
      if (!editor.value) return;
      
      const chain = editor.value.chain().focus();
      
      switch (item.command) {
        case 'toggleBold':
          chain.toggleBold().run();
          break;
        case 'toggleItalic':
          chain.toggleItalic().run();
          break;
        case 'toggleUnderline':
          chain.toggleUnderline().run();
          break;
        case 'toggleStrike':
          chain.toggleStrike().run();
          break;
        case 'toggleHeading':
          chain.toggleHeading({ level: item.level }).run();
          break;
        case 'toggleBulletList':
          chain.toggleBulletList().run();
          break;
        case 'toggleOrderedList':
          chain.toggleOrderedList().run();
          break;
        case 'setTextAlign':
          chain.setTextAlign(item.value).run();
          break;
        case 'setLink':
          // If text is selected, pre-fill with any existing link
          if (editor.value.isActive('link')) {
            const attrs = editor.value.getAttributes('link');
            if (attrs && attrs.href) {
              linkUrl.value = attrs.href;
            }
          }
          showLinkDialog.value = true;
          break;
        case 'addImage':
          addImage();
          break;
        case 'addTable':
          addTable();
          break;
        case 'addHardBreak':
          chain.setHardBreak().run();
          break;
        case 'undo':
          chain.undo().run();
          break;
        case 'redo':
          chain.redo().run();
          break;
      }
    };

    // Check if a toolbar button is active
    const isButtonActive = (item) => {
      if (!editor.value || !editor.value.isActive) return false;
      
      switch (item.command) {
        case 'toggleBold':
          return editor.value.isActive('bold');
        case 'toggleItalic':
          return editor.value.isActive('italic');
        case 'toggleUnderline':
          return editor.value.isActive('underline');
        case 'toggleStrike':
          return editor.value.isActive('strike');
        case 'toggleHeading':
          return editor.value.isActive('heading', { level: item.level });
        case 'toggleBulletList':
          return editor.value.isActive('bulletList');
        case 'toggleOrderedList':
          return editor.value.isActive('orderedList');
        case 'setTextAlign':
          return editor.value.isActive({ textAlign: item.value });
        default:
          return false;
      }
    };

    // Functions for toolbar actions
    const setLink = () => {
      if (linkUrl.value) {
        // Check if link already has http/https prefix
        const url = linkUrl.value.startsWith('http') 
          ? linkUrl.value 
          : `https://${linkUrl.value}`;
          
        editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      } else {
        editor.value.chain().focus().unsetLink().run();
      }
      showLinkDialog.value = false;
      linkUrl.value = '';
    };

    const setColor = (color) => {
      editor.value.chain().focus().setColor(color).run();
    };

    const setHighlight = (color) => {
      editor.value.chain().focus().toggleHighlight({ color }).run();
    };

    const addImage = () => {
      const url = prompt('Enter image URL');
      if (url) {
        editor.value.chain().focus().setImage({ src: url }).run();
      }
    };

    const addTable = () => {
      editor.value.chain().focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    };

    // Add a function to insert line break at the clicked position
    const handleEditorClick = (event) => {
      // Only handle double-clicks to insert line breaks
      if (event.detail === 2 && editor.value) {
        // Stop the default behavior (like text selection)
        event.preventDefault();
        
        // Ensure the editor is focused
        editor.value.commands.focus();
        
        // Insert a hard break at the current position
        editor.value.commands.setHardBreak();
      }
    };

    return {
      isEditorReady,
      editor,
      showLinkDialog,
      linkUrl,
      linkInput,
      currentColor,
      currentHighlight,
      formatButtons,
      headingButtons,
      listButtons,
      alignButtons,
      insertButtons,
      historyButtons,
      setLink,
      setColor,
      setHighlight,
      addImage,
      addTable,
      handleToolbarAction,
      isButtonActive,
      handleEditorClick
    };
  }
};
</script>

<style scoped>
.tiptap-editor {
  margin-bottom: 1.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.editor-rtl {
  direction: rtl;
}

.editor-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.help-text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
}

.error-message {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc2626;
  font-weight: 500;
}

.editor-loading {
  min-height: v-bind('minHeight');
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: #f9fafb;
  color: #6b7280;
}

.loading-spinner {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #e5e7eb;
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-bottom: none;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  background-color: #f9fafb;
  align-items: center;
  gap: 0.125rem;
}

.toolbar-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.toolbar-btn {
  height: 2.25rem;
  min-width: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 0.125rem;
  margin-bottom: 0.125rem;
}

.toolbar-btn:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.toolbar-btn.is-active {
  background-color: #e5e7eb;
  border-color: #d1d5db;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.toolbar-divider {
  width: 1px;
  height: 1.5rem;
  background-color: #e5e7eb;
  margin: 0 0.5rem;
}

.color-picker {
  position: relative;
  display: inline-block;
  margin-right: 0.125rem;
  margin-bottom: 0.125rem;
}

.color-input {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
  cursor: pointer;
}

.color-btn {
  min-width: 2rem;
}

.color-icon {
  color: var(--current-color, #000000);
  font-weight: bold;
}

.highlight-btn {
  min-width: 2rem;
}

.highlight-icon {
  background-color: var(--current-highlight, #FFFF00);
  padding: 0 2px;
  font-weight: bold;
}

.editor-content-wrapper {
  position: relative;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  overflow: hidden;
}

.is-invalid.editor-content-wrapper {
  border-color: #dc2626;
}

:deep(.ProseMirror) {
  min-height: v-bind('minHeight');
  outline: none;
  padding: 1rem;
  background-color: white;
}

:deep(.ProseMirror p) {
  margin: 0.5rem 0;
  line-height: 1.5;
}

:deep(.ProseMirror h1) {
  font-size: 1.5rem;
  margin: 1rem 0 0.75rem;
  font-weight: 600;
}

:deep(.ProseMirror h2) {
  font-size: 1.25rem;
  margin: 0.75rem 0 0.5rem;
  font-weight: 600;
}

:deep(.ProseMirror a) {
  color: #3b82f6;
  text-decoration: underline;
}

:deep(.ProseMirror ul), 
:deep(.ProseMirror ol) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

:deep(.ProseMirror blockquote) {
  border-left: 3px solid #e5e7eb;
  padding-left: 1rem;
  margin-left: 0;
  color: #6b7280;
}

:deep(.ProseMirror table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0.75rem 0;
  overflow: hidden;
}

:deep(.ProseMirror th),
:deep(.ProseMirror td) {
  border: 1px solid #e5e7eb;
  padding: 0.5rem;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

:deep(.ProseMirror th) {
  font-weight: bold;
  background-color: #f9fafb;
}

:deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0.75rem 0;
}

.editor-content {
  cursor: text;
  position: relative;
}

.editor-content:hover {
  background-color: rgba(0, 0, 0, 0.01);
}

.editor-content::after {
  content: "Double-click to insert a line break";
  position: absolute;
  bottom: -24px;
  right: 10px;
  font-size: 0.75rem;
  color: #9ca3af;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.editor-content:hover::after {
  opacity: 0.8;
}

:deep(.ProseMirror br), 
:deep(.ProseMirror br.hard-break) {
  display: block;
  content: "";
  margin: 0.5rem 0;
  border-top: 1px dashed #e5e7eb;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-container {
  background-color: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  animation: dialog-appear 0.2s ease-out;
}

@keyframes dialog-appear {
  0% { transform: scale(0.9); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.dialog-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #9ca3af;
  transition: color 0.2s;
  line-height: 1;
}

.close-btn:hover {
  color: #6b7280;
}

.dialog-body {
  margin-bottom: 1.5rem;
}

.dialog-input {
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.dialog-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-cancel, .btn-primary {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background-color: white;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}

.btn-cancel:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.btn-primary {
  background-color: #3b82f6;
  border: 1px solid #3b82f6;
  color: white;
}

.btn-primary:hover {
  background-color: #2563eb;
  border-color: #2563eb;
}

/* Fade transition for dialog */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style> 